import * as d3 from "d3";
import { scaleLinear } from "d3-scale";
import React, { useRef, useState, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { saveSvgAsPng } from "save-svg-as-png";
import proj4 from "proj4";
import { handleSelectionChange } from "../utils/selectionUtils";
import { COLORS, generateColorMapping, generateLockMapping, getColorCode, getContrastColor } from "../utils/colorUtils";
export default function SuryaBlockMap(props) {
  const {
    data,
    d3Size,
    pierLegends,
    progressColorData,
    lockUnlockData = {},
    currentProjectDetails,
    currentBlockDetails,
    selectedRow,
    setSelectedRow,
    type,
    userSelection,
    boolean
  } = props?.datas;
  let enableGPSLocation = boolean?.enableGPSLocation;
  let controls = boolean?.controls;
  let downloadD3 = boolean?.downloadD3;
  let deviceType = type?.deviceType;
  let activityType = type?.activityType;
  let layoutType = type?.layoutType;
  const [position, setPosition] = useState([0, 0]);
  const [adjust, setAdjust] = useState({
    x: 0,
    y: 0
  });
  const containerRef = useRef();
  const downloadRef = useRef();
  const lineObjs = {};
  const rowObjs = {};
  const sectionObjs = {};
  const labelObjs = {};
  const selectObjs = {};
  const ttObjs = {};
  const pierObjs = {};

  //tracker select 
  const handleSelectTrackerWrapper = async trackerRowData => {
    // Use our utility to update selected rows
    const newSelectedRows = await handleSelectionChange("individualTracker", trackerRowData);
    setSelectedRow(newSelectedRows);
  };

  //enable gps
  useEffect(() => {
    if (enableGPSLocation === true) {
      const watchId = navigator.geolocation.watchPosition(pos => {
        const {
          latitude,
          longitude
        } = pos.coords;
        const [east, north] = proj4(`+proj=utm +zone=60 +north +ellps=WGS84 +datum=WGS84 +units=m +no_defs`).forward([longitude, latitude]);
        setPosition({
          latitude: east,
          longitude: north
        });
      }, err => {
        console.error(err);
      });
      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    } else {
      setPosition([0, 0]);
    }
  }, [enableGPSLocation]);
  const getPrLegend = data => {
    const prlegend = pierLegends[data];
    if (pierLegends && prlegend) {
      return prlegend;
    } else {
      return {
        shape: "circle",
        concentric: false,
        border: "black",
        background: "white",
        width: 1,
        map_color: "#4287f5"
      };
    }
  };
  function ttExterior(ref, ttDataLatitude, pierData01Longitude, pierData02Longitude, color, data) {
    // Determine the stroke and fill colors
    let strokeColor, fillColor;
    if (color === COLORS.COMPLETED) {
      // Completed state - use green
      strokeColor = fillColor = COLORS.COMPLETED;
    } else if (color === COLORS.IN_PROGRESS) {
      // In progress state - use pink
      strokeColor = fillColor = COLORS.IN_PROGRESS;
    } else if (typeof color === 'string') {
      // Any other specific color passed in
      strokeColor = fillColor = color;
    } else if (color === true) {
      // Legacy support for boolean flag (true = completed)
      strokeColor = fillColor = COLORS.COMPLETED;
    } else {
      // Default - use gray
      strokeColor = fillColor = "gray";
    }
    return d3.select(ref.current).append("rect").attr("class", "react-tt cursor-pointer").attr("x", ttDataLatitude - 2).attr("y", pierData02Longitude).attr("width", "5px").attr("height", pierData01Longitude - pierData02Longitude).attr("stroke", strokeColor).style("cursor", "pointer").attr("fill", fillColor);

    // .attr("stroke", isSelected ? "gray" : color ? "#66f1ad" : "gray")
    // .attr("fill", isSelected ? "gray" : color ? "#66f1ad" : "gray")
  }
  function ttInterior(ref, ttDataLatitude, pierData01Longitude, pierData02Longitude, color, data) {
    // Determine the stroke and fill colors
    let strokeColor, fillColor;
    if (color === COLORS.COMPLETED) {
      // Completed state
      strokeColor = COLORS.COMPLETED;
      fillColor = "white";
    } else if (color === COLORS.IN_PROGRESS) {
      // In progress state
      strokeColor = COLORS.IN_PROGRESS;
      fillColor = "white";
    } else if (typeof color === 'string') {
      // Any other specific color
      strokeColor = color;
      fillColor = "white";
    } else if (color === true) {
      // Legacy support for boolean flag (true = completed)
      strokeColor = COLORS.COMPLETED;
      fillColor = "white";
    } else {
      // Default
      strokeColor = "gray";
      fillColor = "white";
    }
    return d3.select(ref.current).append("rect").attr("class", "react-tt cursor-pointer").attr("x", ttDataLatitude - 2).attr("y", pierData02Longitude).attr("width", "5px").attr("height", pierData01Longitude - pierData02Longitude).attr("stroke", strokeColor)
    // .attr("stroke", isSelected ? "gray" : color ? "#66f1ad" : "gray")
    .attr("stroke-width", "1px").style("cursor", "pointer")
    // .attr("fill", isSelected ? "white" : color ? "#66f1ad" : "white")
    .attr("fill", fillColor);
  }
  function ttEdge(ref, pierData01Latitude, pierData01Longitude, pierData02Latitude, pierData02Longitude, color, data) {
    // Determine the stroke color
    let strokeColor;
    if (color === COLORS.COMPLETED) {
      // Completed state
      strokeColor = COLORS.COMPLETED;
    } else if (color === COLORS.IN_PROGRESS) {
      // In progress state
      strokeColor = COLORS.IN_PROGRESS;
    } else if (typeof color === 'string') {
      // Any other specific color
      strokeColor = color;
    } else if (color === true) {
      // Legacy support for boolean flag (true = completed)
      strokeColor = COLORS.COMPLETED;
    } else {
      // Default
      strokeColor = "gray";
    }
    return d3.select(ref.current).append("line").attr("class", "react-tt cursor-pointer").style("cursor", "pointer").attr("x1", pierData01Latitude).attr("y1", pierData01Longitude).attr("x2", pierData02Latitude).attr("y2", pierData02Longitude).attr("stroke-width", "5px").style("cursor", "pointer").attr("stroke", strokeColor)
    // .attr("stroke", isSelected ? "gray" : color ? "#66f1ad" : "gray")
    .attr("stroke-dasharray", "1 1");
  }
  useEffect(() => {
    sectionCallFn();
    // eslint-disable-next-line
  }, [data]);
  const sectionCallFn = async () => {
    try {
      if (data && data.piers) {
        const temp = new Set();

        // Iterate through all piers to collect sections
        for (const trackerRowId in data.piers) {
          const pierArray = data.piers[trackerRowId];
          if (pierArray && pierArray.length > 0) {
            for (const pier of pierArray) {
              temp.add(pier.section);
            }
          }
        }
      }
    } catch (err) {
      console.log(err);
    }
  };
  function isNumber(value) {
    return typeof value === "number";
  }
  function partNumberSplit(data) {
    if (isNumber(data)) {
      return data;
    } else {
      const temp = data?.split(".")[0];
      return temp;
    }
  }
  function reArrangedata(data) {
    let temp = [];
    if (!data || !data.tt) return temp;

    // Flatten TT data
    for (const trackerRowId in data.tt) {
      const ttArray = data.tt[trackerRowId];
      if (ttArray && ttArray.length > 0) {
        temp = [...temp, ...ttArray];
      }
    }
    return temp;
  }
  function reArrangeBlockPierData(data) {
    let temp = [];
    if (!data || !data.piers) return temp;

    // Flatten pier data
    for (const trackerRowId in data.piers) {
      const pierArray = data.piers[trackerRowId];
      if (pierArray && pierArray.length > 0) {
        temp = [...temp, ...pierArray];
      }
    }
    return temp;
  }
  useEffect(() => {
    if (layoutType === "CP" && userSelection?.showType === "torque tube") {
      renderMap(data?.tt);
    } else {
      if (data?.piers) {
        renderMap(data?.piers);
      }
    }
    // eslint-disable-next-line
  }, [data, progressColorData, position, userSelection?.selection, selectedRow, userSelection?.showType, adjust, userSelection?.punchlistStatusSelection]);
  const findSvgWidth = () => {
    const tempTT = new Set();
    const temp = new Set();
    let result;
    if (layoutType === "CP" && userSelection?.showType === "torque tube") {
      reArrangedata(data)?.map(i => tempTT.add(i?.row));
      result = Array.from(tempTT).length * 26;
    } else {
      reArrangeBlockPierData(data)?.map(i => temp.add(i?.row));
      result = Array.from(temp).length * 26;
    }
    return result;
  };
  const renderMap = renderData => {
    if (downloadRef.current) {
      downloadRef.current.innerHTML = "";

      // Add white background rectangle to download SVG - FIRST THING!
      d3.select(downloadRef.current).append("rect").attr("width", "100%").attr("height", "100%").attr("fill", "white");
    }
    containerRef.current.innerHTML = "";
    const wrapper = document.getElementById("wrapper");
    // Generate the color mapping once for the entire rendering process
    const colorMapping = generateColorMapping(progressColorData, layoutType, userSelection, selectedRow, activityType);
    const lockMapping = layoutType === "QC" ? generateLockMapping(lockUnlockData, selectedRow) : {};
    // 1. Fix the isTrackerSelected function to directly check selectedRows
    const isTrackerSelected = trackerId => {
      // Check if this tracker is in the selectedRows
      if (!selectedRow || !selectedRow.sections) return false;

      // Look through all sections to find if this trackerId is selected anywhere
      return Object.values(selectedRow.sections).some(section => section.selectedRows && section.selectedRows[trackerId]);
    };
    const xScalerangeVal = layoutType === "CP" && userSelection?.showType === "pier" ? 100 : 50;
    const xScale = scaleLinear().domain([layoutType === "CP" && userSelection?.showType === "torque tube" ? d3Size?.tt?.minimum_latitude : d3Size?.piers?.minimum_latitude, layoutType === "CP" && userSelection?.showType === "torque tube" ? d3Size?.tt?.maximum_latitude : d3Size?.piers?.maximum_latitude]).range([xScalerangeVal, wrapper.offsetWidth - xScalerangeVal]);
    const yScalerangeVal = layoutType === "CP" && userSelection?.showType === "pier" || type === "lock" ? 100 : 50;
    const yScale = scaleLinear().domain([layoutType === "CP" && userSelection?.showType === "torque tube" ? d3Size?.tt?.maximum_longitude : d3Size?.piers?.maximum_longitude, layoutType === "CP" && userSelection?.showType === "torque tube" ? d3Size?.tt?.minimum_longitude : d3Size?.piers?.minimum_longitude]).range([yScalerangeVal, wrapper.offsetHeight - yScalerangeVal]);
    for (const pr in data?.tt) {
      ttObjs[`trow-${pr}`] = data?.tt[pr];
    }
    for (const pr in renderData) {
      for (const pierIndex in renderData[pr]) {
        const pierData = renderData[pr][pierIndex];
        const lineObj = lineObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}-${pierData["tracker_row_type"]}`];
        if (!lineObj) {
          lineObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}-${pierData["tracker_row_type"]}`] = {
            x: [xScale(pierData["latitude"])],
            y: [yScale(pierData["longitude"])]
          };
        }
        if (lineObj) {
          lineObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}-${pierData["tracker_row_type"]}`] = {
            x: lineObj?.x.concat([xScale(pierData["latitude"])]),
            y: lineObj?.y.concat([yScale(pierData["longitude"])])
          };
        }
        const rowObj = rowObjs[`trow-${pierData["row"]}`];
        if (!rowObj) {
          rowObjs[`trow-${pierData["row"]}`] = {
            x: [xScale(pierData["latitude"])],
            y: [yScale(pierData["longitude"])]
          };
        }
        if (rowObj) {
          rowObjs[`trow-${pierData["row"]}`] = {
            x: rowObj?.x.concat([xScale(pierData["latitude"])]),
            y: rowObj?.y.concat([yScale(pierData["longitude"])])
          };
        }
        const selectObj = selectObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}`];
        if (!selectObj) {
          selectObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}`] = {
            x: [xScale(pierData["latitude"])],
            y: [yScale(pierData["longitude"])]
          };
        }
        if (selectObj) {
          selectObjs[`trow-${pr}-${pierData["tracker_type_id"]}-${pierData["row"]}-${pierData["section"]}`] = {
            x: selectObj?.x.concat([xScale(pierData["latitude"])]),
            y: selectObj?.y.concat([yScale(pierData["longitude"])])
          };
        }
        const sectionObj = sectionObjs[`tsection-${pierData["section"]}`];
        if (!sectionObj) {
          sectionObjs[`tsection-${pierData["section"]}`] = [{
            x: xScale(pierData["latitude"]),
            y: yScale(pierData["longitude"])
          }];
        }
        if (sectionObj) {
          sectionObjs[`tsection-${pierData["section"]}`] = sectionObj.concat([{
            x: xScale(pierData["latitude"]),
            y: yScale(pierData["longitude"])
          }]);
        }
        const labelObj = labelObjs[`tlabel-${pierData["section"]}-${pierData["label"]}`];
        if (!labelObj) {
          labelObjs[`tlabel-${pierData["section"]}-${pierData["label"]}`] = [{
            x: xScale(pierData["latitude"]),
            y: yScale(pierData["longitude"])
          }];
        }
        if (labelObj) {
          labelObjs[`tlabel-${pierData["section"]}-${pierData["label"]}`] = labelObj.concat([{
            x: xScale(pierData["latitude"]),
            y: yScale(pierData["longitude"])
          }]);
        }
        const pierObj = pierObjs[`pier-${getPrLegend(partNumberSplit(pierData.part_number)).shape}-${getPrLegend(partNumberSplit(pierData.part_number)).concentric}-${partNumberSplit(pierData.part_number)}`];
        const pierDatas = {
          bha_sg_labels: pierData?.bha_sg_labels,
          block: pierData?.block,
          block_id: pierData?.block_id,
          id: pierData?.id,
          latitude: xScale(pierData?.latitude),
          longitude: yScale(pierData?.longitude),
          pier_part_order: pierData?.pier_part_order,
          row: pierData?.row,
          section: pierData?.section,
          torque_tube_label: pierData?.torque_tube_label,
          tracker_row_id: pr,
          damper_label: pierData?.damper_label,
          pier_type_motor: pierData?.pier_type_motor,
          tracker_type_id: pierData?.tracker_type_id,
          stroke: getPrLegend(partNumberSplit(pierData?.part_number))?.border ? getPrLegend(partNumberSplit(pierData?.part_number))?.border : "black",
          fill: getPrLegend(partNumberSplit(pierData?.part_number))?.background ? getPrLegend(partNumberSplit(pierData?.part_number))?.background : "white",
          stroke_width: getPrLegend(partNumberSplit(pierData?.part_number))?.width ? getPrLegend(partNumberSplit(pierData?.part_number))?.width : 1,
          map_colour: getPrLegend(partNumberSplit(pierData?.part_number))?.map_colour ? getPrLegend(partNumberSplit(pierData?.part_number))?.map_colour : "#4287f5"
        };
        if (!pierObj) {
          pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData.part_number)}`] = [pierDatas];
        }
        if (pierObj) {
          pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData.part_number)}`] = pierObjs[`pier-${getPrLegend(partNumberSplit(pierData?.part_number))?.shape}-${getPrLegend(partNumberSplit(pierData?.part_number))?.concentric}-${partNumberSplit(pierData.part_number)}`].concat(pierDatas);
        }
      }
    }
    for (const key in rowObjs) {
      const rowNumber = key.split("-")[1];
      if (Math.min.apply(Math, rowObjs[key]?.x) && Math.min.apply(Math, rowObjs[key]?.y) && rowNumber) {
        d3.select(containerRef?.current).append("path").attr("d", "M 6 -5 L 15 0 L 15 9 L 6 15 L -3 9 L -3 0 Z").attr("transform", `translate(${Math.min.apply(Math, rowObjs[key]?.x) - 12 / 2}, ${Math.min.apply(Math, rowObjs[key]?.y) - 30 - 12 / 2})`).attr("stroke", "black").attr("stroke-width", "1px").attr("fill", "white");
        d3.select(containerRef.current).append("text").attr("fill", "black").attr("x", Math.min.apply(Math, rowObjs[key]?.x)).attr("y", Math.min.apply(Math, rowObjs[key]?.y) - 27.5).attr("text-anchor", "middle").style("font-size", "10px").style("font-weight", "600").text(rowNumber);
        d3.select(containerRef.current).append("path").attr("d", "M 6 -5 L 15 0 L 15 9 L 6 15 L -3 9 L -3 0 Z").attr("transform", `translate(${Math.max.apply(Math, rowObjs[key]?.x) - 12 / 2}, ${Math.max.apply(Math, rowObjs[key]?.y) + 40 - 12 / 2})`).attr("stroke", "black").attr("stroke-width", "1px").attr("fill", "white");
        d3.select(containerRef.current).append("text").attr("fill", "black").attr("x", Math.max.apply(Math, rowObjs[key]?.x)).attr("y", Math.max.apply(Math, rowObjs[key]?.y) + 42).attr("text-anchor", "middle").style("font-size", "10px").style("font-weight", "600").text(rowNumber);

        //downloadRef

        d3.select(downloadRef.current).append("path").attr("d", "M 6 -5 L 15 0 L 15 9 L 6 15 L -3 9 L -3 0 Z").attr("transform", `translate(${Math.min.apply(Math, rowObjs[key]?.x) - 12 / 2}, ${Math.min.apply(Math, rowObjs[key]?.y) - 30 - 12 / 2})`).attr("stroke", "black").attr("stroke-width", "1px").attr("fill", "white");
        d3.select(downloadRef.current).append("text").attr("fill", "black").attr("x", Math.min.apply(Math, rowObjs[key]?.x)).attr("y", Math.min.apply(Math, rowObjs[key]?.y) - 27.5).attr("text-anchor", "middle").style("font-size", "10px").style("font-weight", "600").text(rowNumber);
        d3.select(downloadRef.current).append("path").attr("d", "M 6 -5 L 15 0 L 15 9 L 6 15 L -3 9 L -3 0 Z").attr("transform", `translate(${Math.max.apply(Math, rowObjs[key]?.x) - 12 / 2}, ${Math.max.apply(Math, rowObjs[key]?.y) + 40 - 12 / 2})`).attr("stroke", "black").attr("stroke-width", "1px").attr("fill", "white");
        d3.select(downloadRef.current).append("text").attr("fill", "black").attr("x", Math.max.apply(Math, rowObjs[key]?.x)).attr("y", Math.max.apply(Math, rowObjs[key]?.y) + 42).attr("text-anchor", "middle").style("font-size", "10px").style("font-weight", "600").text(rowNumber);
      }
    }

    // Draw locks for QC after all other elements to ensure they're on top
    if (layoutType === "QC") {
      for (const key in selectObjs) {
        // Extract tracker_row_id from the key
        const parts = key.split("-");
        const tracker_row_id = parts[1];

        // Check if this tracker should have a lock icon
        if (lockMapping[tracker_row_id]) {
          // Get the center point of this tracker row
          const centerX = (Math.min.apply(Math, selectObjs[key]?.x) + Math.max.apply(Math, selectObjs[key]?.x)) / 2;
          const centerY = (Math.min.apply(Math, selectObjs[key]?.y) + Math.max.apply(Math, selectObjs[key]?.y)) / 2;

          // Add a white background circle behind the lock for better visibility
          d3.select(containerRef.current).append("circle").attr("cx", centerX).attr("cy", centerY).attr("r", 18) // Larger background
          .attr("fill", "white").attr("opacity", 0.7); // Semi-transparent

          // Lock group
          const lockGroup = d3.select(containerRef.current).append("g").attr("transform", `translate(${centerX}, ${centerY})`);

          // Lock body - make it larger
          lockGroup.append("rect").attr("x", -10).attr("y", -6).attr("width", 20).attr("height", 18).attr("rx", 3).attr("ry", 3).attr("fill", COLORS.LOCKED).attr("opacity", 0.9);

          // Lock shackle - make it larger
          lockGroup.append("path").attr("d", "M -6 -6 C -6 -16, 6 -16, 6 -6").attr("stroke", COLORS.LOCKED).attr("stroke-width", 4).attr("fill", "none").attr("opacity", 0.9);

          // Lock keyhole - make it slightly larger
          lockGroup.append("circle").attr("cx", 0).attr("cy", 3).attr("r", 3).attr("fill", "white").attr("opacity", 0.9);
        }
      }
    }

    // Render selection highlights - now using our isTrackerSelected utility
    if (userSelection?.selection !== "None Selected") {
      for (const key in selectObjs) {
        // Extract tracker_row_id from the key
        const parts = key.split("-");
        const tracker_row_id = parts[1];

        // Check if this tracker is selected
        const isSelected = isTrackerSelected(tracker_row_id);

        // Get the color map entry for this tracker
        let colorKey = "";
        let fillColor;
        if (layoutType === "CP") {
          colorKey = userSelection.selection !== "All" ? `${tracker_row_id}_${String(userSelection.selection).toLowerCase()}` : `${tracker_row_id}_all`;
          const colorData = colorMapping[colorKey];
          if (isSelected) {
            fillColor = COLORS.SELECTED;
          } else if (colorData && (!colorData?.hasIndividualElements || userSelection?.selection === "All")) {
            fillColor = colorData?.mainColor || COLORS.DEFAULT;
          }
        } else if (layoutType === "QC" || layoutType === "Punchlist") {
          colorKey = `${tracker_row_id}_${userSelection?.selection}`;
          fillColor = isSelected ? COLORS.SELECTED : colorMapping[colorKey] || COLORS.DEFAULT;
        }
        // Skip rendering if tracker has individual elements with status unless it's selected or lock type
        if (Math.min.apply(Math, selectObjs[key]?.x) && Math.min.apply(Math, selectObjs[key]?.y)) {
          if (isSelected || fillColor && fillColor !== COLORS.DEFAULT) {
            // Render the tracker highlight
            d3.select(containerRef.current).append("rect").attr("x", Math.min.apply(Math, selectObjs[key]?.x) - 7.5).attr("y", Math.min.apply(Math, selectObjs[key]?.y) - 6).attr("width", "15px").attr("height", Math.max.apply(Math, selectObjs[key]?.y) - Math.min.apply(Math, selectObjs[key]?.y) + 12).attr("fill", fillColor).on("click", () => {
              // Find a pier or tt data for this tracker to pass to the handler
              let elementData;
              if (data?.piers && data?.piers[tracker_row_id] && data?.piers[tracker_row_id].length > 0) {
                elementData = {
                  ...data?.piers[tracker_row_id][0],
                  tracker_row_id
                };
              } else if (data?.tt && data?.tt[tracker_row_id] && data?.tt[tracker_row_id].length > 0) {
                elementData = {
                  ...data?.tt[tracker_row_id][0],
                  tracker_row_id
                };
              } else {
                // Fallback if no pier or tt found
                elementData = {
                  tracker_row_id
                };
              }
              handleSelectTrackerWrapper(elementData);
            });

            // Also add to download reference
            d3.select(downloadRef.current).append("rect").attr("x", Math.min.apply(Math, selectObjs[key]?.x) - 7.5).attr("y", Math.min.apply(Math, selectObjs[key]?.y) - 6).attr("width", "15px").attr("height", Math.max.apply(Math, selectObjs[key]?.y) - Math.min.apply(Math, selectObjs[key]?.y) + 12).attr("fill", fillColor);
          }
        }
      }
    }

    // Torque tube rendering using nested color data structure
    for (const ttKey in ttObjs) {
      const key = ttKey.split("-")[1]; // Get tracker row ID

      // Render each torque tube in this tracker row
      for (const trackerIdx in ttObjs[ttKey]) {
        const pierData01 = data?.piers[key][trackerIdx];
        const ttData = ttObjs[ttKey][trackerIdx];
        const pierData02 = data?.piers[key][Number.parseInt(trackerIdx) + 1];
        let colorKey = "";
        if (layoutType === "CP") {
          colorKey = userSelection.selection !== "All" ? `${key}_${String(userSelection.selection).toLowerCase()}` : `${key}_all`;
        } else if (layoutType === "QC" || layoutType === "Punchlist") {
          colorKey = `${key}_${ttData?.id}_${userSelection?.selection}`;
        }
        const colorData = colorMapping[colorKey] ? colorMapping[colorKey] : COLORS?.DEFAULT;

        // Check if tracker is selected
        const isSelected = isTrackerSelected(key);

        // Skip if missing second pier data
        if (!pierData02) continue;

        // Create data object for tracker row
        const rowData = {
          tracker_row_id: key
        };

        // Determine highlight color for this specific torque tube
        let highlightColor = null;
        if (isSelected) {
          highlightColor = COLORS.SELECTED; // Yellow for selected
        }
        if (layoutType === "CP") {
          if (colorData && colorData?.torqueTubes && colorData?.torqueTubes[ttData?.id]) {
            // Use the color stored in the nested torqueTubes object
            highlightColor = colorData?.torqueTubes[ttData?.id];
          } else if (colorData && colorData?.mainColor && !colorData?.hasIndividualElements) {
            // Use main tracker color if no individual elements have status
            highlightColor = colorData?.mainColor;
          }
        } else if (layoutType === "QC" || layoutType === "Punchlist") {
          highlightColor = colorData;
        }

        // Draw the highlight rectangle if we have a color to use
        if (highlightColor && highlightColor !== COLORS.DEFAULT && highlightColor !== "white" && xScale(ttData?.latitude) && !isSelected) {
          // Calculate the height based on the two pier positions
          const rectHeight = yScale(pierData01?.longitude) - yScale(pierData02?.longitude) + 10;

          // Draw the highlight rectangle
          if (layoutType === "CP" && activityType === "sub_activity" && userSelection?.selection !== "None Selected" || layoutType === "QC" || layoutType === "Punchlist") {
            let width = layoutType === "QC" || layoutType === "Punchlist" ? 20 : 15;
            d3.select(containerRef.current).append("rect").attr("class", `tt-highlight-${ttData.id}`).attr("x", xScale(ttData?.latitude) - width / 2).attr("y", yScale(pierData02?.longitude) - 5).attr("width", width).attr("height", rectHeight).attr("fill", highlightColor).on("click", () => handleSelectTrackerWrapper(ttData));
          }

          // Also add to download reference
          d3.select(downloadRef.current).append("rect").attr("class", `tt-highlight-${ttData.id}`).attr("x", xScale(ttData?.latitude) - 7.5).attr("y", yScale(pierData02?.longitude) - 5).attr("width", 15).attr("height", rectHeight).attr("fill", highlightColor).attr("opacity", 0.6);
        }

        // Now render the TT elements on top with standard colors
        if (ttData.tt_part_tracker_row_type === 0) {
          // Exterior torque tube
          if (xScale(ttData?.latitude)) {
            ttExterior(containerRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false,
            // Don't color the element itself (we've used a background highlight)
            rowData).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "30px").style("font-weight", "700").text(`Section-${ttData?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(ttData));

            // Add to download SVG
            ttExterior(downloadRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false, rowData);
          }
        } else if (ttData.tt_part_tracker_row_type === 1) {
          // Interior torque tube
          if (xScale(ttData?.latitude)) {
            ttInterior(containerRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false,
            // Don't color the element itself
            rowData).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "30px").style("font-weight", "700").text(`Section-${ttData?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(ttData));

            // Add to download SVG
            ttInterior(downloadRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false, rowData);
          }
        } else if (ttData.tt_part_tracker_row_type === 2) {
          // Edge type torque tube
          if (xScale(pierData01?.latitude)) {
            ttEdge(containerRef, xScale(pierData01?.latitude), yScale(pierData01?.longitude), xScale(pierData02?.latitude), yScale(pierData02?.longitude), false,
            // Don't color the element itself
            rowData).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "30px").style("font-weight", "700").text(`Section-${ttData?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(ttData));

            // Add to download SVG
            ttEdge(downloadRef, xScale(pierData01?.latitude), yScale(pierData01?.longitude), xScale(pierData02?.latitude), yScale(pierData02?.longitude), false, rowData);
          }
        } else {
          // Default to exterior for any other type
          if (xScale(ttData?.latitude)) {
            ttExterior(containerRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false,
            // Don't color the element itself
            rowData).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "30px").style("font-weight", "700").text(`Section-${ttData?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(ttData));

            // Add to download SVG
            ttExterior(downloadRef, xScale(ttData?.latitude), yScale(pierData01?.longitude), yScale(pierData02?.longitude), false, rowData);
          }
        }
      }
    }

    // Pier rendering using nested color data structure
    for (const pierData in pierObjs) {
      for (const pierDetails of pierObjs[pierData]) {
        // Get the color entry for this tracker row
        let colorKey = "";
        if (layoutType === "CP") {
          colorKey = userSelection.selection !== "All" ? `${pierDetails.tracker_row_id}_${String(userSelection.selection).toLowerCase()}` : `${pierDetails.tracker_row_id}_all`;
        } else if (layoutType === "QC" || layoutType === "Punchlist") {
          colorKey = `${pierDetails.tracker_row_id}_${pierDetails?.id}_${userSelection?.selection}`;
        }
        const colorData = colorMapping[colorKey] ? colorMapping[colorKey] : COLORS?.DEFAULT;

        // Check if this tracker is selected
        const isSelected = isTrackerSelected(pierDetails.tracker_row_id);

        // Determine the fill color
        let fillColor = pierDetails.fill; // Default

        if (isSelected) {
          // Selection takes priority
          fillColor = COLORS.SELECTED;
        }
        if (layoutType === "CP") {
          if (colorData && colorData?.piers && colorData?.piers && colorData?.piers[pierDetails?.id]) {
            // Use the color stored in the nested piers object
            fillColor = colorData?.piers[pierDetails?.id];
          } else if (colorData && colorData?.mainColor && !colorData?.hasIndividualElements) {
            // Use main tracker color if no individual elements have status
            fillColor = colorData?.mainColor;
          }
        } else if (layoutType === "QC" || layoutType === "Punchlist") {
          fillColor = colorData;
        }
        if (layoutType === "CP" && userSelection?.selection !== "None Selected" && activityType === "sub_activity" && colorData?.piers && colorData?.piers[pierDetails?.id] && !isSelected) {
          d3.select(containerRef.current).append("rect").attr("class", "react-pier-in-progress").attr("x", pierDetails.latitude - (userSelection?.showType === "pier" ? 7.5 : 20)).attr("y", pierDetails.longitude - (userSelection?.showType === "pier" ? 7.5 : 3)).attr("width", userSelection?.showType === "pier" ? "15px" : 40).attr("height", userSelection?.showType === "pier" ? "15px" : 25).attr("stroke", fillColor).attr("fill", fillColor);
        }
        if ((layoutType === "QC" || layoutType === "Punchlist") && !isSelected && fillColor !== "white" && userSelection.selection === "piers") {
          d3.select(containerRef.current).append("rect").attr("class", "react-pier-in-progress").attr("x", pierDetails.latitude - 10).attr("y", pierDetails.longitude - 10).attr("width", "20px").attr("height", "20px").attr("stroke", fillColor).attr("fill", fillColor);
        }

        // In CP type with pier view, or lock/QC type
        if (layoutType === "CP" && userSelection?.showType === "pier" || layoutType === "QC" || layoutType === "Punchlist") {
          // Render pier based on shape with the determined fillColor
          if (pierData.split("-")[1] === "Square") {
            if (pierDetails.latitude && pierDetails.longitude) {
              d3.select(containerRef.current).append("rect").attr("class", `pier-${pierDetails.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails.id}-${pierData.split("-")[3]}`).attr("height", 7).attr("width", 7).attr("x", pierDetails.latitude - 7 / 2).attr("y", pierDetails.longitude - 7 / 2).attr("stroke", pierDetails.stroke).attr("fill", pierDetails.fill) // Use determined fillColor
              .attr("stroke-width", `${pierDetails.stroke_width}px`).on("mouseover", evt => {
                const [mx, my] = d3.pointer(evt);
                tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails.section}`);
              }).on("mouseout", () => {
                tooltip.text("");
              }).on("click", () => handleSelectTrackerWrapper(pierDetails));

              // Add to download SVG
              d3.select(downloadRef.current).append("rect").attr("class", `pier-${pierDetails.id}`).attr("id", `pier-${pierDetails.id}-${pierData.split("-")[3]}`).attr("height", 7).attr("width", 7).attr("x", pierDetails.latitude - 7 / 2).attr("y", pierDetails.longitude - 7 / 2).attr("stroke", pierDetails.stroke).attr("fill", fillColor).attr("stroke-width", `${pierDetails.stroke_width}px`);
            }
          } else if (pierData.split("-")[1] === "Diamond") {
            if (pierDetails?.latitude && pierDetails?.longitude) {
              d3.select(containerRef.current).append("rect").attr("class", `pier-${pierDetails?.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("height", 7).attr("width", 7).attr("x", pierDetails?.latitude - 7 / 2).attr("y", pierDetails?.longitude - 7 / 2).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(45) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", pierDetails?.stroke).attr("fill", pierDetails.fill) // Use determined fillColor
              .attr("stroke-width", `${pierDetails?.stroke_width}px`).on("mouseover", evt => {
                const [mx, my] = d3.pointer(evt);
                tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
              }).on("mouseout", () => {
                tooltip.text("");
              }).on("click", () => handleSelectTrackerWrapper(pierDetails));

              // Add to download SVG
              d3.select(downloadRef.current).append("rect").attr("class", `pier-${pierDetails?.id}`).attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("height", 7).attr("width", 7).attr("x", pierDetails?.latitude - 7 / 2).attr("y", pierDetails?.longitude - 7 / 2).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(45) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", pierDetails?.stroke).attr("fill", fillColor).attr("stroke-width", `${pierDetails?.stroke_width}px`);
            }
          } else if (pierData?.split("-")[1] === "Circle") {
            if (pierDetails?.latitude && pierDetails?.longitude) {
              d3.select(containerRef.current).append("circle").attr("class", `pier-${pierDetails?.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("cx", pierDetails?.latitude).attr("cy", pierDetails?.longitude).attr("r", 4).attr("stroke", pierDetails?.stroke).attr("fill", pierDetails.fill) // Use determined fillColor
              .attr("stroke-width", pierDetails?.stroke_width).on("mouseover", evt => {
                const [mx, my] = d3.pointer(evt);
                tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
              }).on("mouseout", () => {
                tooltip.text("");
              }).on("click", () => handleSelectTrackerWrapper(pierDetails));

              // Add to download SVG
              d3.select(downloadRef.current).append("circle").attr("class", `pier-${pierDetails?.id}`).attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("cx", pierDetails?.latitude).attr("cy", pierDetails?.longitude).attr("r", 4).attr("stroke", pierDetails?.stroke).attr("fill", pierDetails.fill).attr("stroke-width", pierDetails?.stroke_width);
            }
          } else {
            // Default to circle for any other shape
            if (pierDetails?.latitude && pierDetails?.longitude) {
              d3.select(containerRef.current).append("circle").attr("class", `pier-${pierDetails?.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("cx", pierDetails?.latitude).attr("cy", pierDetails?.longitude).attr("r", 4).attr("stroke", pierDetails?.stroke).attr("fill", pierDetails.fill) // Use determined fillColor
              .attr("stroke-width", pierDetails?.stroke_width).on("mouseover", evt => {
                const [mx, my] = d3.pointer(evt);
                tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
              }).on("mouseout", () => {
                tooltip.text("");
              }).on("click", () => handleSelectTrackerWrapper(pierDetails));

              // Add to download SVG
              d3.select(downloadRef.current).append("circle").attr("class", `pier-${pierDetails?.id}`).attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("cx", pierDetails?.latitude).attr("cy", pierDetails?.longitude).attr("r", 4).attr("stroke", pierDetails?.stroke).attr("fill", pierDetails.fill).attr("stroke-width", pierDetails?.stroke_width);
            }
          }

          // Add the crosshair line (diagonal marker) to the pier
          if (pierDetails?.latitude && pierDetails?.longitude) {
            d3.select(containerRef.current).append("line").attr("class", "cursor-pointer").style("cursor", "pointer").attr("x1", pierDetails?.latitude - 1).attr("y1", pierDetails?.longitude - 1).attr("x2", pierDetails?.latitude + 1).attr("y2", pierDetails?.longitude + 1).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(130) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black").on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(pierDetails));

            // Add to download SVG
            d3.select(downloadRef.current).append("line").attr("x1", pierDetails?.latitude - 1).attr("y1", pierDetails?.longitude - 1).attr("x2", pierDetails?.latitude + 1).attr("y2", pierDetails?.longitude + 1).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(130) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black");
          }

          // Damper labels - special X marking for damper piers
          if (layoutType === "CP" && userSelection?.showType === "pier" && pierDetails?.damper_label === "true") {
            if (pierDetails?.latitude && pierDetails?.longitude) {
              // First line of X
              d3.select(containerRef.current).append("line").attr("class", "cursor-pointer").style("cursor", "pointer").attr("x1", pierDetails?.latitude - 6).attr("y1", pierDetails?.longitude - 6).attr("x2", pierDetails?.latitude + 6).attr("y2", pierDetails?.longitude + 6).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(180) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black").attr("stroke-width", 2);

              // Second line of X
              d3.select(containerRef.current).append("line").attr("class", "cursor-pointer").style("cursor", "pointer").attr("x1", pierDetails?.latitude - 6).attr("y1", pierDetails?.longitude - 6).attr("x2", pierDetails?.latitude + 6).attr("y2", pierDetails?.longitude + 6).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(90) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black").attr("stroke-width", 2);

              // Add to download SVG
              d3.select(downloadRef.current).append("line").attr("x1", pierDetails?.latitude - 6).attr("y1", pierDetails?.longitude - 6).attr("x2", pierDetails?.latitude + 6).attr("y2", pierDetails?.longitude + 6).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(180) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black").attr("stroke-width", 2);
              d3.select(downloadRef.current).append("line").attr("x1", pierDetails?.latitude - 6).attr("y1", pierDetails?.longitude - 6).attr("x2", pierDetails?.latitude + 6).attr("y2", pierDetails?.longitude + 6).attr("transform", `translate(${pierDetails?.latitude}, ${pierDetails?.longitude}) rotate(90) translate(-${pierDetails?.latitude}, -${pierDetails?.longitude})`).attr("stroke", "black").attr("stroke-width", 2);
            }
          }
        } else {
          // For non-pier view types (e.g., part number view, BHA view)
          const colorCountData = getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends);
          const colorCountArr = colorCountData?.split("-");
          if (colorCountArr?.length > 0) {
            // Handle multi-color parts (split into multiple colored rectangles)
            colorCountArr.map((i, index) => {
              d3.select(containerRef.current).append("rect").attr("class", `nonpier-${pierDetails?.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("height", 20 / colorCountArr.length).attr("width", 35).attr("x", pierDetails?.latitude - 35 / 2).attr("y", pierDetails?.longitude + index * (20 / colorCountArr.length)).attr("stroke", () => {
                return i;
              }).attr("fill", () => {
                return i;
              }).attr("stroke-width", pierDetails?.stroke_width).on("mouseover", evt => {
                const [mx, my] = d3.pointer(evt);
                tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
              }).on("mouseout", () => {
                tooltip.text("");
              }).on("click", () => handleSelectTrackerWrapper(pierDetails));
              d3.select(downloadRef.current).append("rect").attr("class", `nonpier-${pierDetails?.id}-download`).attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}-download`).attr("height", 20 / colorCountArr.length).attr("width", 35).attr("x", pierDetails?.latitude - 35 / 2).attr("y", pierDetails?.longitude + index * (20 / colorCountArr.length)).attr("stroke", i).attr("fill", i).attr("stroke-width", pierDetails?.stroke_width);
              return null; // Since this is inside a map
            });
          } else {
            // Single color rectangle
            d3.select(containerRef.current).append("rect").attr("class", `nonpier-${pierDetails?.id} cursor-pointer`).style("cursor", "pointer").attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("height", 20).attr("width", 35).attr("x", pierDetails?.latitude - 35 / 2).attr("y", pierDetails?.longitude).attr("stroke", () => {
              const value = getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends);
              return value;
            }).attr("fill", () => {
              const value = getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends);
              return value;
            }).attr("stroke-width", pierDetails?.stroke_width).on("mouseover", evt => {
              const [mx, my] = d3.pointer(evt);
              tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
            }).on("mouseout", () => {
              tooltip.text("");
            }).on("click", () => handleSelectTrackerWrapper(pierDetails));

            // Add to download SVG
            d3.select(downloadRef.current).append("rect").attr("class", `nonpier-${pierDetails?.id}`).attr("id", `pier-${pierDetails?.id}-${pierData?.split("-")[3]}`).attr("height", 20).attr("width", 35).attr("x", pierDetails?.latitude - 35 / 2).attr("y", pierDetails?.longitude).attr("stroke", getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends)).attr("fill", getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends)).attr("stroke-width", pierDetails?.stroke_width);
          }
          // Add text labels on top of the colored rectangles
          d3.select(containerRef.current).append("text").attr("class", "cursor-pointer").style("cursor", "pointer").attr("fill", getContrastColor(getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends))).attr("x", pierDetails?.latitude).attr("y", pierDetails?.longitude + 13).attr("text-anchor", "middle").style("font-size", "9px").style("font-weight", "600").text(() => {
            if (layoutType === "CP" && userSelection?.showType === "bha") {
              if (pierDetails?.bha_sg_labels === "Extra Heavy") {
                return "E. Heavy";
              } else {
                return pierDetails?.bha_sg_labels;
              }
            } else {
              return pierData?.split("-")[3];
            }
          }).on("mouseover", evt => {
            const [mx, my] = d3.pointer(evt);
            tooltip.attr("x", mx + 2).attr("y", my + 32).attr("fill", "black").style("font-size", "32px").style("font-weight", "700").text(`Section-${pierDetails?.section}`);
          }).on("mouseout", () => {
            tooltip.text("");
          }).on("click", () => handleSelectTrackerWrapper(pierDetails));

          // Add to download SVG
          d3.select(downloadRef.current).append("text").attr("fill", getContrastColor(getColorCode(pierData?.split("-")[3], pierDetails, layoutType, userSelection, pierLegends))).attr("x", pierDetails?.latitude).attr("y", pierDetails?.longitude + 13).attr("text-anchor", "middle").style("font-size", "9px").style("font-weight", "600").text(() => {
            if (layoutType === "CP" && userSelection?.showType === "bha") {
              if (pierDetails?.bha_sg_labels === "Extra Heavy") {
                return "E. Heavy";
              } else {
                return pierDetails?.bha_sg_labels;
              }
            } else {
              return pierData?.split("-")[3];
            }
          });
        }

        // Render motor indicators for torque tube view
        if (layoutType === "CP" && userSelection?.showType === "torque tube") {
          const motorData = data?.piers[parseInt(pierDetails?.tracker_row_id)].find(o => o?.pier_type_motor === 1);
          if (motorData) {
            // Draw motor circle
            d3.select(containerRef.current).append("circle").attr("class", `motor-pier-circle`).attr("id", `motor-pier-${motorData?.id}-${Number.parseInt(pierDetails?.tracker_row_id)}`).attr("r", 8).attr("cx", xScale(motorData?.latitude)).attr("cy", yScale(motorData?.longitude) + 10).attr("fill", "#F16253");

            // Add "M" text inside motor circle
            d3.select(containerRef.current).append("text").attr("class", `motor-pier-text`).attr("fill", "white").attr("id", `motor-pier-text-${motorData?.id}-${Number.parseInt(pierDetails?.tracker_row_id)}`).attr("x", xScale(motorData?.latitude)).attr("y", yScale(motorData?.longitude) + 13).attr("text-anchor", "middle").style("font-size", "9px").style("font-weight", "600").text("M");
          }
        }
      }
    }
    // ADD THE MOTOR INDICATORS DOWNLOAD CODE HERE:
    // Render motor indicators for download SVG (avoid duplicates by tracking processed tracker rows)
    if (layoutType === "CP" && userSelection?.showType === "torque tube") {
      const processedTrackerRows = new Set();
      for (const pierData in pierObjs) {
        for (const pierDetails of pierObjs[pierData]) {
          const trackerRowId = pierDetails?.tracker_row_id;

          // Only process each tracker row once
          if (!processedTrackerRows.has(trackerRowId)) {
            processedTrackerRows.add(trackerRowId);
            const motorData = data?.piers[parseInt(trackerRowId)]?.find(o => o?.pier_type_motor === 1);
            if (motorData) {
              // Draw motor circle for download
              d3.select(downloadRef.current).append("circle").attr("class", `motor-pier-circle-download`).attr("r", 8).attr("cx", xScale(motorData?.latitude)).attr("cy", yScale(motorData?.longitude) + 10).attr("fill", "#F16253");

              // Add "M" text inside motor circle for download
              d3.select(downloadRef.current).append("text").attr("class", `motor-pier-text-download`).attr("fill", "white").attr("x", xScale(motorData?.latitude)).attr("y", yScale(motorData?.longitude) + 13).attr("text-anchor", "middle").style("font-size", "9px").style("font-weight", "600").text("M");
            }
          }
        }
      }
    }

    // Rest of Download Ref rendering and GPS location code omitted for brevity

    if (deviceType !== "web" && enableGPSLocation) {
      d3.select(containerRef.current).append("circle").attr("fill", "#A8BEE2").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 9);
      d3.select(containerRef.current).append("circle").attr("fill", "white").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 5);
      d3.select(containerRef.current).append("circle").attr("fill", "#3A72B6").attr("cx", xScale(position?.latitude)).attr("cy", yScale(position?.longitude)).attr("r", 4); // Adjust the radius as needed
    }
    d3.select("#download").on("click", async () => {
      if (downloadD3) {
        downloadD3(true);
      }
      try {
        // Small delay to allow UI to update with loading state
        await new Promise(resolve => setTimeout(resolve, 100));
        let temp;
        if (layoutType === "CP" && userSelection?.showType === "pier") {
          temp = "Layout";
        } else if (layoutType === "CP" && userSelection?.showType === "part number") {
          temp = "Pier";
        } else if (layoutType === "CP" && userSelection?.showType === "torque tube") {
          temp = "TT";
        } else if (layoutType === "CP" && userSelection?.showType === "bha") {
          temp = "BHA & SG";
        } else {
          temp = "pier";
        }
        saveSvgAsPng(document.getElementsByClassName("download-svg")[0], `${currentProjectDetails?.project_number}_${currentBlockDetails?.name}_ ${temp} map.png`, {
          scale: 3.8,
          backgroundColor: 'white' // Belt and suspenders approach
        });
      } catch (error) {
        console.error("Download failed:", error);
      } finally {
        // Reset loading state
        if (downloadD3) {
          downloadD3(false);
        }
      }
    });
    // Then immediately after this code, you should have:
    const tooltip = d3.select(containerRef.current).append("text").attr("class", "tooltip").attr("fill", "white").style("pointer-events", "none");
  };
  if (deviceType === "web") {
    const transCompHeightVal = userSelection?.showType === "part number" ? 500 : userSelection?.showType === "torque tube" ? 700 : 0;
    const transCompWidthVal = layoutType === "CP" && userSelection?.showType === "pier" ? 0 : findSvgWidth();
    return /*#__PURE__*/React.createElement("div", {
      style: {
        position: "relative",
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: "calc(100vh - 220px)"
      }
    }, /*#__PURE__*/React.createElement(TransformWrapper, {
      minScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_min_scale),
      initialScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_initial_scale),
      initialPositionX: (window.innerWidth - 710 - Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_min_scale) * currentProjectDetails?.svg_dimension?.web?.web_d3_width) / 2,
      initialPositionY: (window.innerHeight - 300 - Number.parseFloat(currentProjectDetails?.svg_dimension?.web?.web_d3_min_scale) * currentProjectDetails?.svg_dimension?.web?.web_d3_height) / 2
    }, /*#__PURE__*/React.createElement(TransformComponent, {
      contentStyle: {
        height: `${currentProjectDetails?.svg_dimension?.web?.web_d3_height + transCompHeightVal + adjust.y}px`,
        width: `${currentProjectDetails?.svg_dimension?.web?.web_d3_width + transCompWidthVal + adjust.x}px`
      },
      wrapperStyle: {
        height: "100%",
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: `${currentProjectDetails?.svg_dimension?.web?.web_d3_height + transCompHeightVal + adjust.y}px`,
        width: `${currentProjectDetails?.svg_dimension?.web?.web_d3_width + transCompWidthVal + adjust.x}px`
      },
      id: "wrapper"
    }, /*#__PURE__*/React.createElement("svg", {
      ref: containerRef,
      style: {
        width: "100%",
        height: "100%",
        cursor: "grab"
      }
    }))))), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "none"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("svg", {
      ref: downloadRef,
      className: "download-svg" // <-- Add this class!
      ,
      style: {
        height: `${currentProjectDetails?.svg_dimension?.web?.web_d3_height + transCompHeightVal}px`,
        width: `${currentProjectDetails?.svg_dimension?.web?.web_d3_width + transCompWidthVal}px`
      }
    }))), controls && /*#__PURE__*/React.createElement("div", {
      style: {
        left: 0,
        bottom: 1,
        backgroundColor: "transparent",
        position: "absolute"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "33%",
        left: "33%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "Adjust"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "42%",
        left: "35%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "view"), /*#__PURE__*/React.createElement("svg", {
      version: "1.0",
      xmlns: "http://www.w3.org/2000/svg",
      width: "100.000000pt",
      height: "100.000000pt",
      viewBox: "0 0 120.000000 120.000000",
      preserveAspectRatio: "xMidYMid meet"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "0",
      y: "0",
      width: "120",
      height: "120",
      rx: "10",
      ry: "10",
      fill: "none"
    }), /*#__PURE__*/React.createElement("g", {
      transform: "translate(0.000000,104.000000) scale(0.100000,-0.100000)",
      fill: "#000000",
      stroke: "none"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "540",
      cy: "520",
      r: "200",
      fill: "#a0a0a0",
      stroke: "#000000",
      strokeWidth: "5",
      sx: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        center: !adjust.center
      })
    }, /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "705",
      textAnchor: "middle",
      fontSize: "40",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "Adjust"), /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "745",
      textAnchor: "middle",
      fontSize: "30",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "View")), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y - 100
      }),
      style: {
        cursor: "pointer"
      },
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M295 240 l-70 -70 30 -25 c90 -75 233 -112 360 -95 71 10 185 59 234\r 101 l25 20 -72 72 c-68 68 -73 71 -94 58 c-62 -40 -85 -46 -163 -46 c-69 0 -87\r 4 -129 28 c-27 15 -49 27 -50 27 c-1 0 -33 -32 -71 -70z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M828 773 l-67 -68 25 -42 c46 -76 45 -210 -2 -279 l-25 -37 72 -73\r 72 -73 28 36 c106 132 127 324 54 488 -21 49 -70 115 -84 115 -3 0 -36 -30\r -73 -67z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M438 995\r c-66 -16 -139 -52 -183 -89\r l-30 -25 70 -70 70 -71 60 31\r c53 27 68 30 131 27 52 -2 84 -10 121 -30\r l51 -26 68 69 c76 76 76 79 0 126\r c-96 61 -249 86 -358 58z"
    }), /*#__PURE__*/React.createElement("path", {
      style: {
        cursor: "pointer"
      },
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M156 805 c-89 -116 -113 -296 -61 -445 15 -45 82 -150 94 -150 4 0\r 37 30 73 68 l67 67 -27 55 c-39 79 -38 172 2 253 l28 59 -63 64 c-35 35 -69\r 64 -75 64 -6 0 -24 -16 -38 -35z"
    })))));
  } else {
    const transCompHeight = userSelection?.showType === "pier" ? 0 : 850;
    const transCompWidth = layoutType === "CP" && userSelection?.showType === "pier" ? 0 : findSvgWidth();
    const transCompHeightVal = currentProjectDetails?.svg_dimension?.tablet?.tab_d3_height / 2 + transCompHeight;
    const transCompWidthVal = (currentProjectDetails?.svg_dimension?.tablet?.tab_d3_width + 500) / 2 + transCompWidth;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      style: {
        height: "calc(100vh - 240px)"
      }
    }, /*#__PURE__*/React.createElement(TransformWrapper, {
      minScale: Number.parseFloat(currentProjectDetails?.svg_dimension?.tablet?.tab_svg_min_scale),
      alignmentAnimation: {
        disabled: true
      }
    }, /*#__PURE__*/React.createElement(TransformComponent, {
      contentStyle: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      wrapperStyle: {
        height: "100%",
        width: "100%"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        height: `${transCompHeightVal + adjust.y}px`,
        width: `${transCompWidthVal + adjust.x}px`
      },
      id: "wrapper"
    }, /*#__PURE__*/React.createElement("svg", {
      ref: containerRef,
      style: {
        height: "100%",
        width: "100%"
      }
    }))))), controls && /*#__PURE__*/React.createElement("div", {
      style: {
        position: "absolute",
        left: 0,
        bottom: 10,
        backgroundColor: "transparent"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "33%",
        left: "33%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "Adjust"), /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        top: "42%",
        left: "35%",
        fontSize: "10px",
        color: "white",
        position: "absolute"
      }
    }, "view"), /*#__PURE__*/React.createElement("svg", {
      version: "1.0",
      xmlns: "http://www.w3.org/2000/svg",
      width: "100.000000pt",
      height: "100.000000pt",
      viewBox: "0 0 120.000000 120.000000",
      preserveAspectRatio: "xMidYMid meet"
    }, /*#__PURE__*/React.createElement("rect", {
      x: "0",
      y: "0",
      width: "120",
      height: "120",
      rx: "10",
      ry: "10",
      fill: "none"
    }), /*#__PURE__*/React.createElement("g", {
      transform: "translate(0.000000,104.000000) scale(0.100000,-0.100000)",
      fill: "#000000",
      stroke: "none"
    }, /*#__PURE__*/React.createElement("circle", {
      cx: "540",
      cy: "520",
      r: "200",
      fill: "#a0a0a0",
      stroke: "#000000",
      strokeWidth: "5",
      onClick: () => setAdjust({
        ...adjust,
        center: !adjust.center
      })
    }, /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "705",
      textAnchor: "middle",
      fontSize: "40",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "Adjust"), /*#__PURE__*/React.createElement("text", {
      x: "700",
      y: "745",
      textAnchor: "middle",
      fontSize: "30",
      fill: "#fffff",
      fontFamily: "Arial, sans-serif"
    }, "View")), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M295 240 l-70 -70 30 -25 c90 -75 233 -112 360 -95 71 10 185 59 234\r 101 l25 20 -72 72 c-68 68 -73 71 -94 58 c-62 -40 -85 -46 -163 -46 c-69 0 -87\r 4 -129 28 c-27 15 -49 27 -50 27 c-1 0 -33 -32 -71 -70z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M828 773 l-67 -68 25 -42 c46 -76 45 -210 -2 -279 l-25 -37 72 -73\r 72 -73 28 36 c106 132 127 324 54 488 -21 49 -70 115 -84 115 -3 0 -36 -30\r -73 -67z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        y: adjust.y + 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M438 995\r c-66 -16 -139 -52 -183 -89\r l-30 -25 70 -70 70 -71 60 31\r c53 27 68 30 131 27 52 -2 84 -10 121 -30\r l51 -26 68 69 c76 76 76 79 0 126\r c-96 61 -249 86 -358 58z"
    }), /*#__PURE__*/React.createElement("path", {
      onClick: () => setAdjust({
        ...adjust,
        x: adjust.x - 100
      }),
      fill: "#d4d3d2",
      stroke: "#000000",
      strokeWidth: "5",
      d: "M156 805 c-89 -116 -113 -296 -61 -445 15 -45 82 -150 94 -150 4 0\r 37 30 73 68 l67 67 -27 55 c-39 79 -38 172 2 253 l28 59 -63 64 c-35 35 -69\r 64 -75 64 -6 0 -24 -16 -38 -35z"
    })))));
  }
}